<template>
    <ComponentWithPropertiesInstance v-if="loadedComponent" :key="loadedComponent.key" :component="loadedComponent" v-bind="$attrs" />
    <div v-else class="st-view import-error-view">
        <STNavigationBar :title="$t(`%gT`)" />
        <main>
            <h1>{{ $t('%gT') }}</h1>
            <p>{{ $t('%1VA') }}</p>

            <p>
                <LoadingButton :loading="loading">
                    <button class="button primary" type="button" @click="retry">
                        <span class="icon retry" />
                        <span>{{ $t('%1EU') }}</span>
                    </button>
                </LoadingButton>
            </p>
        </main>
    </div>
</template>

<script setup lang="ts">
import { ComponentWithProperties } from '@browser/navigation';
import { ComponentWithPropertiesInstance } from '@browser/navigation';
import { ref, shallowRef } from 'vue';

import LoadingButton from '#navigation/LoadingButton.vue';
import STNavigationBar from '#navigation/STNavigationBar.vue';
import { Toast } from '#overlays/Toast.ts';

const props = defineProps<{
    /**
     * Loads the component that should replace this error view once the network is available again.
     * Typically includes its own retry-with-backoff behaviour (see importWithRetry).
     */
    load: () => Promise<ComponentWithProperties>;
}>();

const loadedComponent = shallowRef<ComponentWithProperties | null>(null);
const loading = ref(false);

async function retry() {
    if (loading.value) {
        return;
    }
    loading.value = true;

    try {
        // On success this replaces the error view with the actual component.
        const c = await props.load();
        if (c instanceof ComponentWithProperties) {
            loadedComponent.value = c;
        } else {
            console.error('Unexpected load type. Expected ComponentWithProperties, received ', c);
            throw new Error('Unexpected load type');
        }
    } catch (e) {
        console.error(e);
        new Toast($t(`%1YJ`), 'error red').show();
    } finally {
        loading.value = false;
    }
}
</script>

<style lang="scss">
.import-error-view {
    .icon.retry {
        margin-right: 5px;
    }
}
</style>
